import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SquaresFour, List, FunnelSimple } from "@phosphor-icons/react";
import PropertyCard from "@/components/PropertyCard";
import api from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useCities } from "@/lib/useCities";
import { Checkbox } from "@/components/ui/checkbox";
import FilterChips from "@/components/FilterChips";
import { formatINR } from "@/lib/format";

const COMMERCIAL_TYPES = [
  ["office", "Office"], ["shop", "Shop"], ["showroom", "Showroom"],
  ["warehouse", "Warehouse"], ["industrial", "Industrial"],
  ["commercial_land", "Commercial Land"], ["other", "Other"],
];

const CHIP_LABELS = {
  listing_type: v => (v === "rent" ? "Rent" : "Buy"),
  bhk: v => `${v} BHK`,
  property_type: v => COMMERCIAL_TYPES.find(([k]) => k === v)?.[1] || v,
  price_min: v => `Min ${formatINR(Number(v))}`,
  price_max: v => `Max ${formatINR(Number(v))}`,
  verified: () => "Verified only",
  rera: () => "RERA registered",
  featured: () => "Featured",
};

export default function Properties({ fixedCategory }) {
  const isCommercial = fixedCategory === "commercial";
  const [sp, setSp] = useSearchParams();
  const cityOptions = useCities();
  const [layout, setLayout] = useState("grid");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("newest");

  const params = Object.fromEntries(sp.entries());

  const update = (key, value) => {
    const next = new URLSearchParams(sp);
    if (value === "" || value === undefined || value === null) next.delete(key);
    else next.set(key, value);
    setSp(next);
    setPage(1);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qp = new URLSearchParams(sp);
    if (fixedCategory) qp.set("category", fixedCategory); // dedicated category pages lock the classification
    qp.set("page", String(page));
    qp.set("sort", sort);
    qp.set("page_size", "12");
    api.get(`/properties?${qp.toString()}`).then(r => {
      if (cancelled) return;
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    }).catch(() => {
      if (cancelled) return;
      setItems([]); setTotal(0); // API failure must never leave the page stuck on "Loading…"
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sp, page, sort]);

  const filters = (
    <div className="space-y-5">
      <FilterGroup label="Listing Type">
        <Select value={params.listing_type || ""} onValueChange={v => update("listing_type", v)}>
          <SelectTrigger data-testid="filter-listing-type" className="rounded-lg border-slate-200"><SelectValue placeholder="Any" /></SelectTrigger>
          <SelectContent><SelectItem value="sale">Buy</SelectItem><SelectItem value="rent">Rent</SelectItem></SelectContent>
        </Select>
      </FilterGroup>
      {!fixedCategory && (
      <FilterGroup label="Category">
        <Select value={params.category || ""} onValueChange={v => {
          const next = new URLSearchParams(sp);
          if (v) next.set("category", v); else next.delete("category");
          if (v === "commercial") next.delete("bhk"); else next.delete("property_type");
          setSp(next); setPage(1);
        }}>
          <SelectTrigger data-testid="filter-category" className="rounded-lg border-slate-200"><SelectValue placeholder="Any" /></SelectTrigger>
          <SelectContent><SelectItem value="residential">Residential</SelectItem><SelectItem value="commercial">Commercial</SelectItem></SelectContent>
        </Select>
      </FilterGroup>
      )}
      <FilterGroup label="City">
        <Select value={params.city || ""} onValueChange={v => update("city", v)}>
          <SelectTrigger data-testid="filter-city" className="rounded-lg border-slate-200"><SelectValue placeholder="Any city" /></SelectTrigger>
          <SelectContent>{cityOptions.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </FilterGroup>
      {isCommercial || params.category === "commercial" ? (
        <FilterGroup label="Commercial Sub-Category">
          <Select value={params.property_type || ""} onValueChange={v => update("property_type", v)}>
            <SelectTrigger data-testid="filter-commercial-type" className="rounded-lg border-slate-200"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>{COMMERCIAL_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </FilterGroup>
      ) : (
        <FilterGroup label="BHK">
          <Select value={params.bhk || ""} onValueChange={v => update("bhk", v)}>
            <SelectTrigger data-testid="filter-bhk" className="rounded-lg border-slate-200"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>{[1, 2, 3, 4, 5].map(n => <SelectItem key={n} value={String(n)}>{n} BHK</SelectItem>)}</SelectContent>
          </Select>
        </FilterGroup>
      )}
      <FilterGroup label="Price Range (₹)">
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="filter-price-min" type="number" placeholder="Min" value={params.price_min || ""} onChange={e => update("price_min", e.target.value)} className="rounded-lg border-slate-200" />
          <Input data-testid="filter-price-max" type="number" placeholder="Max" value={params.price_max || ""} onChange={e => update("price_max", e.target.value)} className="rounded-lg border-slate-200" />
        </div>
      </FilterGroup>
      <div className="space-y-3 pt-2">
        <label className="flex items-center gap-2 text-sm cursor-pointer text-slate-700">
          <Checkbox data-testid="filter-verified" checked={params.verified === "true"} onCheckedChange={v => update("verified", v ? "true" : "")} /> Verified only
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer text-slate-700">
          <Checkbox data-testid="filter-rera" checked={params.rera === "true"} onCheckedChange={v => update("rera", v ? "true" : "")} /> RERA registered
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer text-slate-700">
          <Checkbox data-testid="filter-featured" checked={params.featured === "true"} onCheckedChange={v => update("featured", v ? "true" : "")} /> Featured
        </label>
      </div>
      <button data-testid="clear-filters" onClick={() => setSp({})} className="w-full text-sm text-blue-600 border border-blue-200 py-2.5 rounded-lg hover:bg-blue-50 font-medium transition-colors">Clear filters</button>
    </div>
  );

  return (
    <div>
      <div className="section-blue">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs text-slate-500 mb-2"><a href="/" className="hover:text-blue-600">Home</a> › {isCommercial ? "Commercial Properties" : "Properties"}</div>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">{isCommercial ? "Commercial Properties" : params.listing_type === "rent" ? "Properties for Rent" : "Properties for Buy"}</h1>
              <div className="text-sm text-slate-600 mt-1">{loading ? "Searching…" : `${total} listings found`}</div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Sheet>
                <SheetTrigger asChild>
                  <button data-testid="filters-btn" className="lg:hidden flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-sm bg-white text-slate-700"><FunnelSimple size={16} /> Filters</button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80 overflow-y-auto bg-white"><div className="mt-8">{filters}</div></SheetContent>
              </Sheet>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="sort-select" className="w-44 bg-white border-slate-200 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="price_low">Price: Low to High</SelectItem>
                  <SelectItem value="price_high">Price: High to Low</SelectItem>
                  <SelectItem value="area_low">Area: Low to High</SelectItem>
                  <SelectItem value="area_high">Area: High to Low</SelectItem>
                  <SelectItem value="most_viewed">Most Viewed</SelectItem>
                  <SelectItem value="featured">Featured</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="layout-toggle">
                {[["grid", SquaresFour, ""], ["list", List, "hidden md:inline-flex"]].map(([k, Icon, extra]) => (
                  <button key={k} data-testid={`layout-${k}`} onClick={() => setLayout(k)}
                    className={`p-2.5 ${extra} ${layout === k ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-blue-50 hover:text-blue-600"} transition-colors`} aria-label={k}>
                    <Icon size={18} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-8">
          <aside className="hidden lg:block card-premium p-6 h-fit sticky top-24">{filters}</aside>

          <div>
            <FilterChips params={params} update={update} exclude={isCommercial ? ["category"] : []} labels={CHIP_LABELS} />
            {layout === "list" ? (
              <>
                <div className="space-y-4 hidden md:block">{items.map(p => <PropertyCard key={p.id} p={p} layout="list" />)}</div>
                <div className="grid grid-cols-1 gap-6 md:hidden">{items.map(p => <PropertyCard key={p.id} p={p} />)}</div>
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {items.map(p => <PropertyCard key={p.id} p={p} />)}
              </div>
            )}
            {!loading && items.length === 0 && (
              <div className="text-center py-20 text-slate-500 bg-blue-50 rounded-xl border border-blue-100">No properties match your filters. Try adjusting them.</div>
            )}
            {items.length > 0 && (
              <div className="flex justify-center gap-2 mt-10">
                <button data-testid="page-prev" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 disabled:opacity-40 transition-colors">Previous</button>
                <span className="px-4 py-2 text-sm text-slate-600">Page {page}</span>
                <button data-testid="page-next" disabled={items.length < 12} onClick={() => setPage(p => p + 1)} className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 disabled:opacity-40 transition-colors">Next</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">{label}</div>
      {children}
    </div>
  );
}
